import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";

const POLL_MS = 4000;
const errMsg = (e, d) => e?.response?.data?.detail || d;

export const useAdminLock = () => {
  const [state, setState] = useState({ locked: false, configured: false });
  const refresh = useCallback(() => api.get("/admin-lock/status", { silent: true })
    .then((r) => setState(r.data)).catch(() => {}), []);
  useEffect(() => {
    refresh();
    const t = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, POLL_MS);
    const onLocked = () => setState((s) => ({ ...s, locked: true }));
    window.addEventListener("admin-locked", onLocked);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(t); window.removeEventListener("admin-locked", onLocked); window.removeEventListener("focus", refresh); };
  }, [refresh]);
  const lock = async () => {
    try { await api.post("/admin-lock/lock"); setState((s) => ({ ...s, locked: true })); }
    catch (e) { toast.error(errMsg(e, "Não foi possível bloquear")); }
  };
  return { ...state, lock, setUnlocked: () => setState((s) => ({ ...s, locked: false })) };
};

const ForgotDialog = ({ open, onOpenChange, onUnlocked }) => {
  const [pwd, setPwd] = useState("");
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setPwd(""); setHint(null); } }, [open]);
  const call = async (path, ok) => {
    setBusy(true);
    try { const r = await api.post(path, { password: pwd }); ok(r.data); }
    catch (e) { toast.error(errMsg(e, "Senha incorreta")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm z-[210]" data-testid="lock-forgot-dialog">
        <DialogHeader><DialogTitle>Esqueci a senha do bloqueio</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Digite a senha que você usa para entrar no sistema.</p>
        <PasswordInput autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Senha do login" data-testid="lock-login-password" />
        {hint === null ? (
          <Button disabled={!pwd || busy} onClick={() => call("/admin-lock/hint", (d) => setHint(d.hint))} className="w-full" data-testid="lock-show-hint">Ver dica da senha</Button>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="lock-hint">
              <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">Dica</p>
              <p className="text-sm text-amber-900 break-words">{hint || "Nenhuma dica cadastrada."}</p>
            </div>
            <Button variant="outline" disabled={busy} onClick={() => call("/admin-lock/unlock-with-login", () => { onOpenChange(false); onUnlocked(); })} className="w-full" data-testid="lock-unlock-with-login">
              Destravar com a senha do login
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminLockScreen({ onUnlocked }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const done = () => { setPwd(""); toast.success("Área Adm desbloqueada"); onUnlocked(); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin-lock/unlock", { password: pwd }); done(); }
    catch (err) { toast.error(errMsg(err, "Senha incorreta")); setPwd(""); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] bg-[#1e3a8a] flex items-center justify-center p-6" data-testid="admin-lock-screen">
      <form onSubmit={submit} className="w-full max-w-xs text-center text-white space-y-5 animate-in fade-in zoom-in-95 duration-300">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center"><Lock className="w-8 h-8" /></div>
        <div>
          <p className="font-heading text-2xl font-black">Área Adm bloqueada</p>
          <p className="text-sm text-white/70 mt-1">Digite a senha do bloqueio para continuar.</p>
        </div>
        <PasswordInput autoFocus autoComplete="off" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Senha do bloqueio"
          className="h-12 text-center text-lg bg-white text-slate-900" data-testid="lock-password-input" />
        <Button type="submit" disabled={!pwd || busy} className="w-full h-12 rounded-full bg-white text-[#1e3a8a] hover:bg-white/90 font-bold" data-testid="lock-unlock-btn">
          <KeyRound className="w-4 h-4 mr-2" />{busy ? "Verificando…" : "Desbloquear"}
        </Button>
        <button type="button" onClick={() => setForgot(true)} className="text-sm text-white/80 underline underline-offset-4 hover:text-white" data-testid="lock-forgot-btn">Esqueci a senha</button>
      </form>
      <ForgotDialog open={forgot} onOpenChange={setForgot} onUnlocked={onUnlocked} />
    </div>
  );
}
